import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

interface CreateDisputeDialogProps {
  orderId: string;
  orderNumber: string;
  productTitle: string;
  onDisputeCreated?: () => void;
}

const CreateDisputeDialog = ({ orderId, orderNumber, productTitle, onDisputeCreated }: CreateDisputeDialogProps) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async () => {
    if (!user || !reason || !description) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.from('order_disputes').insert({
        order_id: orderId,
        user_id: user.id,
        reason,
        description,
        status: 'pending'
      });

      if (error) throw error;

      // Get user profile for name
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();

      // Send email notification
      await supabase.functions.invoke('send-dispute-notification', {
        body: {
          orderId,
          orderNumber,
          reason,
          description,
          clientName: profile?.full_name || 'Client'
        }
      });

      toast.success('Réclamation créée avec succès');
      setOpen(false);
      setReason('');
      setDescription('');
      onDisputeCreated?.();
    } catch (error) {
      console.error('Error creating dispute:', error);
      toast.error('Erreur lors de la création de la réclamation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <AlertCircle className="h-4 w-4 mr-2" />
          Signaler un problème
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Créer une réclamation</DialogTitle>
          <DialogDescription>
            Commande {orderNumber} - {productTitle}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Motif de la réclamation</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionnez un motif" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="defective_product">Produit défectueux</SelectItem>
                <SelectItem value="wrong_item">Mauvais article</SelectItem>
                <SelectItem value="missing_item">Article manquant</SelectItem>
                <SelectItem value="damaged">Produit endommagé</SelectItem>
                <SelectItem value="not_as_described">Non conforme à la description</SelectItem>
                <SelectItem value="other">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Description détaillée</Label>
            <Textarea
              placeholder="Décrivez le problème rencontré avec votre commande..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !reason || !description}
            className="w-full"
          >
            {submitting ? 'Envoi en cours...' : 'Envoyer la réclamation'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDisputeDialog;
